import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import SEOSchema from '@/components/feature/SEOSchema';
import { categories, comics } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = Number(id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const comic = comics.find((c) => c.id === articleId);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-emotion-sad-line text-2xl" />
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">抱歉，您要找的漫画不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line" />
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const category = categories.find((c) => c.slug === comic.category);
  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const formatViews = (views: number) => {
    if (views >= 10000) {
      return (views / 10000).toFixed(1) + '万';
    }
    return views.toString();
  };

  return (
    <>
      <SEOSchema type="article" articleId={articleId} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category?.name || '分类', href: `/category/${comic.category}` },
                { label: comic.title },
              ]}
            />

            <article className="bg-background-50 rounded-2xl p-6 md:p-8 mb-10">
              <div className="flex flex-col md:flex-row gap-8">
                <div className="shrink-0 w-full md:w-[280px]">
                  <div className="relative overflow-hidden rounded-xl aspect-[5/7]">
                    <img
                      src={comic.coverImage}
                      alt={comic.title}
                      className="w-full h-full object-cover object-top"
                      title={`${comic.title} - 韩漫屋韩国漫画在线`}
                    />
                  </div>
                </div>

                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                    {comic.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="text-sm text-foreground-500">
                      作者：<strong className="text-foreground-800">{comic.author}</strong>
                    </span>
                    <span className="text-foreground-300">|</span>
                    <span className="text-sm text-foreground-500">
                      分类：
                      <Link
                        to={`/category/${comic.category}`}
                        className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
                      >
                        {category?.name || comic.category}
                      </Link>
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-4 mb-6">
                    <div className="flex items-center gap-1.5">
                      <span className="px-2.5 py-1 bg-primary-100 text-primary-700 text-sm font-bold rounded-md">
                        {comic.rating}
                      </span>
                      <span className="text-xs text-foreground-400">评分</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-foreground-500">
                      <i className="ri-eye-line" />
                      <span>{formatViews(comic.views)} 阅读</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-foreground-500">
                      <i className="ri-file-list-3-line" />
                      <span>{comic.chapters} 话</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-foreground-500">
                      <i className="ri-calendar-line" />
                      <span>更新于 {comic.updateDate}</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {comic.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <p className="text-sm text-foreground-600 leading-relaxed mb-8">
                    {comic.description}
                  </p>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button className="flex-1 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer">
                      <i className="ri-play-line mr-2" />
                      开始阅读
                    </button>
                    <button className="flex-1 px-6 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 font-semibold rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer">
                      <i className="ri-bookmark-line mr-2" />
                      收藏漫画
                    </button>
                  </div>
                </div>
              </div>
            </article>

            {relatedComics.length > 0 && (
              <section>
                <h2 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                  {relatedComics.map((c) => (
                    <ComicCard key={c.id} comic={c} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}